/**
 * RecentRecordToast
 * ─────────────────
 * 最近操作记录 - 悬浮倒计时浮窗
 *
 * 行为：
 *  - 每次创建/选中最新运单后，从底部弹入（slide-up + fade-in 动画）
 *  - 右侧 SVG 环形进度条倒计时（默认 6 秒）
 *  - 倒计时结束自动收起；期间可点击 🖨打印 / 🗑删除 / ✕ 立即关闭
 *  - 点击 "全部记录" 按钮展开历史抽屉
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shipment } from '../../../services/shipment.service';
import { useScannerStore } from '../../../store/scanner.store';

const COUNTDOWN_S = 6;          // 自动消失秒数
const RING_R = 16;          // SVG 环半径
const RING_STROKE = 3;           // 线宽
const CIRCUMFERENCE = 2 * Math.PI * RING_R;

// ── 单条历史行 ──────────────────────────────────────────────────
const RecordRow: React.FC<{
    s: Shipment;
    isAnomaly: boolean;
    onDelete: () => void;
    onPrint: () => void;
}> = ({ s, isAnomaly, onDelete, onPrint }) => {
    const modeIcon = s.transport_mode === 2 ? 'sailing' : s.transport_mode === 3 ? 'flight' : 'local_shipping';
    const modeLabel = s.transport_mode === 2 ? '海运' : s.transport_mode === 3 ? '空运' : '陆运';
    return (
        <div className={`flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 ${isAnomaly ? 'bg-red-500/5' : ''}`}>
            <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className={`font-mono text-xs font-black tracking-wider truncate ${isAnomaly ? 'text-red-400' : 'text-white'}`}>
                        {s.tracking_no}
                    </span>
                    {isAnomaly && <span className="material-icons text-red-400 text-sm animate-pulse flex-shrink-0">warning</span>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-black text-primary">{(s.weight || 0).toFixed(2)} kg</span>
                    <span className="text-[10px] text-gray-500">
                        {new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {(s.length || s.width || s.height) && (
                        <span className="text-[10px] text-gray-500">
                            {s.length}×{s.width}×{s.height}cm
                        </span>
                    )}
                    {s.shipper_name && (
                        <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold">{s.shipper_name}</span>
                    )}
                    {s.transport_mode && (
                        <span className="text-[10px] bg-white/5 text-gray-400 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            <span className="material-icons text-[10px]">{modeIcon}</span>{modeLabel}
                        </span>
                    )}
                    {isAnomaly && (
                        <span className="text-[9px] bg-red-500/15 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded font-bold">
                            重量异常
                        </span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <button
                    onClick={onDelete}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all active:scale-90"
                >
                    <span className="material-icons text-base">delete_outline</span>
                </button>
                <button
                    onClick={onPrint}
                    className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-all active:scale-90"
                >
                    <span className="material-icons text-base">print</span>
                </button>
            </div>
        </div>
    );
};

// ── 倒计时环 ───────────────────────────────────────────────────
const CountdownRing: React.FC<{ progress: number }> = ({ progress }) => {
    const size = (RING_R + RING_STROKE) * 2;
    const center = RING_R + RING_STROKE;
    const dash = CIRCUMFERENCE * progress;

    return (
        <svg width={size} height={size} className="rotate-[-90deg]" aria-hidden>
            {/* Track */}
            <circle
                cx={center} cy={center} r={RING_R}
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={RING_STROKE}
            />
            {/* Progress */}
            <circle
                cx={center} cy={center} r={RING_R}
                fill="none"
                stroke="#135bec"
                strokeWidth={RING_STROKE}
                strokeLinecap="round"
                strokeDasharray={`${dash} ${CIRCUMFERENCE}`}
                style={{ transition: 'stroke-dasharray 0.25s linear' }}
            />
        </svg>
    );
};

// ── 主组件 ─────────────────────────────────────────────────────
export interface RecentRecordsProps {
    shipments: Shipment[] | undefined;
    /** 每次外部创建新运单时传入最新那条 (或 null) */
    latestShipment: Shipment | null;
    handleDelete: (id: string) => void;
    handlePrint: (s: Shipment) => void;
}

const RecentRecords: React.FC<RecentRecordsProps> = ({
    shipments,
    latestShipment,
    handleDelete,
    handlePrint,
}) => {
    const { weightAuditAbs, weightAuditPercent } = useScannerStore();

    // 浮窗状态
    const [visible, setVisible] = useState(false);
    const [progress, setProgress] = useState(1);          // 1 = full ring, 0 = empty
    const [showHistory, setShowHistory] = useState(false);

    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownMs = COUNTDOWN_S * 1000;

    const dismiss = useCallback(() => {
        setVisible(false);
        setShowHistory(false);
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    const checkAnomaly = (s: Shipment) => {
        if (!s.weight) return false;
        const check = (otherW?: number) => {
            if (otherW == null) return false;
            const diff = Math.abs(s.weight - otherW);
            return diff > weightAuditAbs || (diff / s.weight) * 100 > weightAuditPercent;
        };
        return check(s.transit_weight) || check(s.receiver_weight);
    };

    // 每次 latestShipment 变化 → 重新触发浮窗 + 重置倒计时
    useEffect(() => {
        if (!latestShipment) return;

        // 清理旧 timer
        if (timerRef.current) clearInterval(timerRef.current);

        setVisible(true);
        setShowHistory(false);
        setProgress(1);

        const startMs = Date.now();
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startMs;
            const remaining = Math.max(0, 1 - elapsed / countdownMs);
            setProgress(remaining);
            if (remaining <= 0) {
                dismiss();
            }
        }, 60);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [latestShipment?.id]); // 仅依赖 id，避免数据更新时重触发

    if (!visible || !latestShipment) return null;

    const isAnomaly = checkAnomaly(latestShipment);
    const allRecords = shipments ?? [];
    const remainingSec = Math.ceil(progress * COUNTDOWN_S);

    return (
        <>
            {/* ── 全屏遮罩（仅历史展开时显示） ── */}
            {showHistory && (
                <div
                    className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
                    onClick={dismiss}
                />
            )}

            {/* ── 悬浮卡片 ── */}
            <div
                className={`fixed bottom-[80px] left-4 right-4 z-50 max-w-lg mx-auto
                    animate-drawer-up origin-bottom`}
            >
                {/* 历史记录抽屉（展开时显示在卡片上方） */}
                {showHistory && allRecords.length > 1 && (
                    <div className="mb-2 bg-[#1a2332]/95 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl animate-fade-in max-h-64 overflow-y-auto no-scrollbar">
                        <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#1a2332]/95 backdrop-blur-xl z-10">
                            <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                                全部记录 ({allRecords.length})
                            </span>
                            <button onClick={() => setShowHistory(false)} className="text-gray-500 hover:text-gray-300 transition-colors">
                                <span className="material-icons text-base">expand_more</span>
                            </button>
                        </div>
                        {/* Skip first (shown in main card), show rest */}
                        {allRecords.slice(1).map(s => (
                            <RecordRow
                                key={s.id}
                                s={s}
                                isAnomaly={checkAnomaly(s)}
                                onDelete={() => handleDelete(s.id)}
                                onPrint={() => handlePrint(s)}
                            />
                        ))}
                    </div>
                )}

                {/* 主卡片 */}
                <div className={`bg-[#1a2332]/95 backdrop-blur-xl rounded-2xl border shadow-2xl overflow-hidden ${isAnomaly ? 'border-red-500/40' : 'border-white/10'
                    }`}>
                    {/* 顶部: 运单信息 + 倒计时环 */}
                    <div className="flex items-center gap-3 px-4 pt-3.5 pb-3">
                        {/* 状态图标 */}
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isAnomaly ? 'bg-red-500/15 text-red-400' : 'bg-green-500/15 text-green-400'
                            }`}>
                            <span className="material-icons text-xl">
                                {isAnomaly ? 'warning' : 'check_circle'}
                            </span>
                        </div>

                        {/* 运单信息 */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                                    {isAnomaly ? '⚠ 重量异常' : '✓ 创建成功'}
                                </span>
                            </div>
                            <p className="font-mono text-sm font-black text-white truncate mt-0.5">
                                {latestShipment.tracking_no}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-xs font-bold text-primary">
                                    {(latestShipment.weight || 0).toFixed(2)} kg
                                </span>
                                {(latestShipment.length || latestShipment.width || latestShipment.height) && (
                                    <span className="text-[10px] text-gray-500">
                                        {latestShipment.length}×{latestShipment.width}×{latestShipment.height}cm
                                    </span>
                                )}
                                {latestShipment.receiver_name && (
                                    <span className="text-[10px] text-indigo-400 font-bold bg-indigo-500/10 px-1.5 py-0.5 rounded">
                                        → {latestShipment.receiver_name}
                                    </span>
                                )}
                                {latestShipment.shipper_name && (
                                    <span className="text-[10px] text-primary/80 font-bold bg-primary/5 px-1.5 py-0.5 rounded">
                                        {latestShipment.shipper_name}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* 倒计时环 + 关闭 */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                            <div className="relative w-9 h-9 flex items-center justify-center">
                                <CountdownRing progress={progress} />
                                <span className="absolute text-[10px] font-black text-gray-400 tabular-nums">
                                    {remainingSec}
                                </span>
                            </div>
                            <button
                                onClick={dismiss}
                                className="text-gray-600 hover:text-gray-300 transition-colors"
                                aria-label="关闭"
                            >
                                <span className="material-icons text-base">close</span>
                            </button>
                        </div>
                    </div>

                    {/* 分隔 + 操作按钮行 */}
                    <div className="flex items-center border-t border-white/5">
                        {/* 全部记录按钮 */}
                        <button
                            onClick={() => setShowHistory(s => !s)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-gray-500 hover:text-gray-300 transition-all border-r border-white/5 hover:bg-white/3"
                        >
                            <span className="material-icons text-sm">history</span>
                            全部记录
                            {allRecords.length > 1 && (
                                <span className="bg-white/10 text-gray-400 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                                    {allRecords.length}
                                </span>
                            )}
                            <span className={`material-icons text-sm transition-transform duration-200 ${showHistory ? 'rotate-180' : ''}`}>
                                expand_less
                            </span>
                        </button>

                        {/* 打印按钮 */}
                        <button
                            onClick={() => { handlePrint(latestShipment); dismiss(); }}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-primary hover:text-primary/80 hover:bg-primary/5 transition-all border-r border-white/5"
                        >
                            <span className="material-icons text-sm">print</span>
                            打印面单
                        </button>

                        {/* 删除按钮 */}
                        <button
                            onClick={() => { handleDelete(latestShipment.id); dismiss(); }}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-gray-500 hover:text-red-400 hover:bg-red-500/5 transition-all"
                        >
                            <span className="material-icons text-sm">delete_outline</span>
                            删除
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RecentRecords;
